import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronUp, Home } from 'lucide-react';
import { useNavigationSource } from '@/hooks/useNavigationSource';
import { useScrollHeader } from '@/hooks/useScrollHeader';

/**
 * Sticky navigation buttons that appear when scrolled past hero section.
 * - Top left: Back button (Home icon for direct links, ChevronLeft for internal navigation)
 * - Bottom right: Scroll to top button
 */
export function ScrollingBackButton() {
  const navigate = useNavigate();
  const { isDirectLink } = useNavigationSource();
  const { isVisible } = useScrollHeader();

  const handleBack = () => {
    if (isDirectLink) {
      navigate('/');
    } else {
      navigate(-1);
    }
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* Back button - top left */}
      <div
        style={{
          ...styles.topContainer,
          transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
          opacity: isVisible ? 1 : 0,
          pointerEvents: isVisible ? 'auto' : 'none',
        }}
      >
        <button
          style={styles.button}
          onClick={handleBack}
          aria-label={isDirectLink ? 'Go to home' : 'Go back'}
        >
          {isDirectLink ? (
            <Home size={24} color="white" />
          ) : (
            <ChevronLeft size={24} color="white" />
          )}
        </button>
      </div>

      {/* Scroll to top button - bottom right */}
      <button
        style={{
          ...styles.scrollTopButton,
          transform: isVisible ? 'scale(1)' : 'scale(0)',
          opacity: isVisible ? 1 : 0,
          pointerEvents: isVisible ? 'auto' : 'none',
        }}
        onClick={handleScrollToTop}
        aria-label="Scroll to top"
      >
        <ChevronUp size={24} color="white" />
      </button>
    </>
  );
}

const styles = {
  topContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '12px',
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0))',
    zIndex: 1001,
    transition: 'transform 0.25s ease-out, opacity 0.25s ease-out',
  } as React.CSSProperties,

  button: {
    background: 'rgba(0,0,0,0.4)',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
    minWidth: '44px',
    minHeight: '44px',
  } as React.CSSProperties,

  scrollTopButton: {
    position: 'fixed',
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 75px)',
    right: '24px',
    background: 'rgba(0,0,0,0.5)',
    border: 'none',
    borderRadius: '50%',
    padding: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.25s ease-out, opacity 0.25s ease-out, background 0.2s ease',
    minWidth: '48px',
    minHeight: '48px',
    zIndex: 1001,
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  } as React.CSSProperties,
};
